import * as React from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/ui/components/ui/switch";
import { VisibilityEyeIcon } from "@/ui/components/icons/VisibilityEye";

export interface VisibilityToggleProps {
  /**
   * Current visibility state
   */
  visible: boolean;

  /**
   * Callback when visibility changes
   */
  onToggle: () => void;

  /**
   * Label text
   * @default "Show on public profile"
   */
  label?: string;

  /**
   * Whether to show the label text
   * @default true
   */
  showLabel?: boolean;

  /**
   * Additional className for the container
   */
  className?: string;

  /**
   * Disabled switch
   */
  disabled?: boolean;
}

/**
 * VisibilityToggle Component
 *
 * A toggle switch with eye icon inside the thumb for controlling visibility.
 * Used for wallet and social link visibility in the Profile page.
 *
 * Design:
 * - Eye icon (􀋭) rendered inside the switch thumb
 * - Icon is white when checked (on gray-500 background)
 * - Icon is gray-500 when unchecked (on white background)
 * - Label text appears to the right of the switch
 *
 * Features:
 * - Eye icon integrated in switch thumb
 * - Switch component integration
 * - Customizable label text
 *
 * @example
 * ```tsx
 * <VisibilityToggle
 *   visible={true}
 *   onToggle={() => handleToggle()}
 *   label="Show on public profile"
 * />
 * ```
 */
export const VisibilityToggle = React.forwardRef<
  HTMLDivElement,
  VisibilityToggleProps
>(
  (
    {
      visible,
      onToggle,
      label = "Show on public profile",
      showLabel = true,
      className,
      disabled = false,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center justify-center py-2", className)}
      >
        <Switch
          checked={visible}
          disabled={disabled}
          onCheckedChange={onToggle}
          aria-label={label}
          thumbIcon={<VisibilityEyeIcon size={8} weight="bold" />}
        />
        {showLabel && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-montserrat text-gray-700 ml-2">
              {label}
            </span>
          </div>
        )}
      </div>
    );
  }
);

VisibilityToggle.displayName = "VisibilityToggle";
